<script setup lang="ts">
import type { AlertSocketMessageModel } from '@/types/socket/SocketAlertModel'
import { io, type Socket } from 'socket.io-client'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AlertWrapper from '../components/Alerts/AlertWrapper.vue'
import SnackbarItem from '../components/Alerts/SnackbarItem.vue'
import store from '../store'
import { EColors } from '../types/constants/ColorValues'
import { SOCKET_RETRY_DELAY } from './socketUtils'

const DEVICE_URL = `${import.meta.env.VITE_SOCKET_URL ?? window.location.origin}/device`

type Props = {
    device_ids?: number[]
}
const props = defineProps<Props>()
type Emits = {
    (e: 'update', value: AlertSocketMessageModel[]): void
}
const emit = defineEmits<Emits>()

const socketState = ref<Props>()

const socketDeviceAlerts = ref<AlertSocketMessageModel[]>([])
const connected = ref(true)
const loading = ref(false)

const getNewAccessToken = () => store?.state?.authentication?.accessToken ?? ''

const socketAlert: Socket = io(DEVICE_URL, {
    autoConnect: false,
    retries: 5,
    path: '/ws/device',
    transports: ['websocket'],
    query: {
        device_ids: socketState.value?.device_ids,
        token: getNewAccessToken()
    }
})
    .on('connect', () => {
        connected.value = socketAlert.connected
        loading.value = false
    })
    .on('disconnect', () => (connected.value = socketAlert.connected))
    .on('connect_error', res => {
        if (res?.message === 'Authentication error') {
            connected.value = false
            socketAlert.io.opts.query = {
                ...socketAlert.io.opts.query,
                token: store?.state?.authentication?.accessToken ?? ''
            }
            setTimeout(() => socketAlert.disconnect().connect(), SOCKET_RETRY_DELAY)
        }
    })
    .on('message', (res: AlertSocketMessageModel) => {
        const foundIndex = socketDeviceAlerts.value.findIndex(
            item => item.device_id === res.device_id
        )
        if (foundIndex !== -1) socketDeviceAlerts.value[foundIndex] = res
        emit('update', socketDeviceAlerts.value)
    })

const setSocket = (device_ids: number[]) => {
    if (
        !device_ids?.every(
            item => socketState.value?.device_ids?.find(deviceId => deviceId === item)
        )
    ) {
        updateSocketState('device_ids', props.device_ids)
        reloadSocket()
    }
}

const reloadSocket = () => {
    loading.value = true
    if (socketAlert.connected) socketAlert.disconnect()
    socketAlert.connect()
}

const updateSocketState = (key: string, value?: number[]) => {
    if (value) socketDeviceAlerts.value = value?.map(item => ({ device_id: item }))
    socketState.value = { ...socketState.value, [key]: value }
    socketAlert.io.opts.query = { ...socketAlert.io.opts.query, [key]: value }
}

onMounted(() => setSocket(props.device_ids ?? []))
watch(
    () => props.device_ids,
    newValue => setSocket(newValue ?? []),
    { immediate: true }
)

onBeforeUnmount(() => {
    socketAlert.disconnect()
})
</script>
<template>
    <div v-if="!connected && !loading" class="notices is-top">
        <AlertWrapper>
            <SnackbarItem
                :type="EColors.DANGER"
                :message="$t('socket.connection.disconnected')"
                class="alert-type alert-component" />
        </AlertWrapper>
    </div>
    <slot v-bind="$attrs" />
</template>
